import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Loader,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  UserCircleIcon,
  UsersIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { config } from "@/shared/lib/config";
import { formatDate } from "@/shared/lib/date-time";
import { formatStudentDisplayName } from "@/modules/students/domain/lib/student-display-name";
import { getInitials } from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { PublicStudentProfileDTO } from "../../domain/contracts/public-student-profile";
import { publicBookingApi } from "../api/client";

export function PublicStudentProfilePage() {
  const { memberId } = useParams({ from: "/public/student/$memberId" });
  const navigate = useNavigate();
  const [data, setData] = useState<PublicStudentProfileDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!memberId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    publicBookingApi
      .getPublicStudentProfile(memberId)
      .then((res) => {
        if (!cancelled) {
          setData(res);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
        <div className="text-muted-foreground">
          <Loader />
        </div>
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
        <p className="font-medium text-foreground">ບໍ່ພົບຂໍ້ມູນສະມາຊິກ</p>
        <Button
          variant="outline"
          onClick={() => navigate({ to: "/app/dashboard" })}
        >
          <ArrowLeftIcon className="size-4" />
          ກັບຄືນ
        </Button>
      </div>
    );
  }

  const {
    student,
    activeClassPackages,
    activePrivateLessonPackages,
    activeClassPackage,
    activePrivateLessonPackage,
    attendanceHistory,
  } = data;

  const classPackages =
    activeClassPackages.length > 0
      ? activeClassPackages
      : activeClassPackage
        ? [activeClassPackage]
        : [];

  const privatePackages =
    activePrivateLessonPackages.length > 0
      ? activePrivateLessonPackages
      : activePrivateLessonPackage
        ? [activePrivateLessonPackage]
        : [];
  const isDeactivated = student.status === "deactivated";

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto max-w-2xl px-4 py-6">
        {/* Header */}
        {/* <div className="mb-6 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: "/app/dashboard" })}
            className="text-muted-foreground"
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
          <span className="text-muted-foreground text-sm uppercase tracking-wide">
            ໂປຣໄຟລ໌ສາທາລະນະ
          </span>
        </div> */}

        {isDeactivated && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            <p className="text-sm">ບັນຊີນີ້ຖືກປິດການໃຊ້ງານແລ້ວ</p>
          </div>
        )}

        {/* Member Information */}
        <Card className="mb-4">
          {/* <CardHeader>
            <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm uppercase tracking-wide">
              <UserIcon className="size-4" />
              ຂໍ້ມູນສະມາຊິກ
            </CardTitle>
          </CardHeader> */}
          <CardContent className="flex items-start gap-4">
            <Avatar className="h-14 w-14 shrink-0">
              <AvatarImage
                src={resolveImageSrc(student.image) ?? undefined}
                alt={formatStudentDisplayName(student.name, student.nickname)}
              />
              <AvatarFallback>
                {getInitials(
                  formatStudentDisplayName(student.name, student.nickname),
                )}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-lg">
                {formatStudentDisplayName(student.name, student.nickname)}
              </p>
              <p className="mt-1 text-muted-foreground text-sm">
                ເລກສະມາຊິກ: {student.memberId}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Active Class Package */}
        {classPackages.map((activeClassPackage) => (
          <Card key={`${activeClassPackage.packageName}-${activeClassPackage.expireDate}`} className="mb-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm uppercase tracking-wide">
                <UsersIcon className="size-4" />
                ແພັກເກດຄລາດສ໌ທີ່ໃຊ້ຢູ່
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="font-medium">{activeClassPackage.packageName}</p>

              {/* Check if package has selected classes */}
              {activeClassPackage.selectedClasses &&
              activeClassPackage.selectedClasses.length > 0 ? (
                /* Multiple Progress Bars - One per Class */
                <div className="space-y-4">
                  {activeClassPackage.selectedClasses.map((c, index) => {
                    const classUsedSessions =
                      c.allocatedSessions - c.remainingSessions;
                    const classUsedPercentage =
                      c.allocatedSessions > 0
                        ? Math.round(
                            (classUsedSessions / c.allocatedSessions) * 100,
                          )
                        : 0;

                    return (
                      <div key={c.id} className="space-y-2">
                        {index > 0 && <div className="border-t pt-4" />}
                        <div className="text-center">
                          <h4 className="font-semibold text-sm">
                            {c.classTypeName} - {c.levelName}
                          </h4>
                        </div>
                        {/* Usage Summary for this class */}
                        <div className="flex justify-between text-center text-sm">
                          <span className="font-semibold">
                            ໃຊ້ແລ້ວ: {classUsedSessions} ຄັ້ງ
                          </span>{" "}
                          <span className="text-muted-foreground">
                            ທັງໝົດ: {c.allocatedSessions} ຄັ້ງ
                          </span>
                        </div>
                        {/* Progress Bar */}
                        <Progress value={classUsedPercentage} className="h-2" />
                      </div>
                    );
                  })}
                  {/* Overall Summary */}
                  {/* <div className="mt-4 rounded-lg border bg-muted/50 p-3">
                    <div className="text-center text-sm">
                      <span className="font-semibold">
                        ລວມໃຊ້ແລ້ວ:{" "}
                        {activeClassPackage.totalSessions -
                          activeClassPackage.remainingSessions}{" "}
                        ຄັ້ງ
                      </span>{" "}
                      <span className="text-muted-foreground">
                        ລວມທັງໝົດ: {activeClassPackage.totalSessions} ຄັ້ງ
                      </span>
                    </div>
                    <Progress
                      value={
                        activeClassPackage.totalSessions > 0
                          ? Math.round(
                              ((activeClassPackage.totalSessions -
                                activeClassPackage.remainingSessions) /
                                activeClassPackage.totalSessions) *
                                100,
                            )
                          : 0
                      }
                      className="mt-2 h-2"
                    />
                  </div> */}
                </div>
              ) : (
                /* Single Chart - Overall (backward compatibility) */
                <>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground text-sm">ຄົງເຫຼືອ</span>
                    <span className="font-semibold text-primary">
                      {activeClassPackage.remainingSessions} /{" "}
                      {activeClassPackage.totalSessions} ຄັ້ງ
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{
                        width: `${
                          activeClassPackage.totalSessions > 0
                            ? Math.round(
                                (activeClassPackage.remainingSessions /
                                  activeClassPackage.totalSessions) *
                                  100,
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </>
              )}

              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <CalendarIcon className="size-4" />
                <span>ຫມົດອາຍຸ: {formatDate(activeClassPackage.expireDate)}</span>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Active Private Lesson Package */}
        {privatePackages.map((activePrivateLessonPackage) => (
          <Card
            key={`${activePrivateLessonPackage.packageName}-${activePrivateLessonPackage.expireDate}-${activePrivateLessonPackage.membershipRole}`}
            className="mb-4"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm uppercase tracking-wide">
                <UserCircleIcon className="size-4" />
                {activePrivateLessonPackage.membershipRole === "member"
                  ? "ແພັກເກດຮ່ວມທີ່ເຂົ້າຮ່ວມ"
                  : "ແພັກເກດຮຽນສ່ວນຕົວທີ່ໃຊ້ຢູ່"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-medium">{activePrivateLessonPackage.packageName}</p>
              {activePrivateLessonPackage.membershipRole === "member" &&
                activePrivateLessonPackage.ownerStudentName && (
                  <p className="text-muted-foreground text-sm">
                    ສະມາຊິກແພັກເກດຮ່ວມ · ຜູ້ຈ່າຍ{" "}
                    {activePrivateLessonPackage.ownerStudentName}
                  </p>
                )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="text-muted-foreground text-sm">ຄົງເຫຼືອ </span>
                  <span className="ml-2 font-semibold">
                    {activePrivateLessonPackage.remainingHours} ຊົ່ວໂມງ
                  </span>
                </div>
                <Badge variant="default" className="bg-green-600">
                  ໃຊ້ງານຢູ່
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <CalendarIcon className="size-4" />
                <span>
                  ຫມົດອາຍຸ: {formatDate(activePrivateLessonPackage.expireDate)}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm uppercase tracking-wide">
              <ClockIcon className="size-4" />
              ປະຫວັດການເຂົ້າຮຽນ
            </CardTitle>
            <Badge
              variant="secondary"
              className="text-muted-foreground text-xs"
            >
              ລາຍການລ່າສຸດ 20
            </Badge>
          </CardHeader>
          <CardContent>
            {attendanceHistory.length === 0 ? (
              <p className="py-6 text-center text-muted-foreground text-sm">
                ບໍ່ມີປະຫວັດ
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-muted-foreground text-xs uppercase">
                      ວັນທີ
                    </TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase">
                      ຊື່ຄລາດສ໌ / ວິຊາ
                    </TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase">
                      ປະເພດ
                    </TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase">
                      ສະຖານະ
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendanceHistory.map((row, i) => (
                    <TableRow key={`${row.date}-${row.sessionName}-${i}`}>
                      <TableCell className="text-sm">
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {row.sessionName}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            row.type === "class" ? "default" : "secondary"
                          }
                          className={
                            row.type === "private"
                              ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200"
                              : ""
                          }
                        >
                          {row.type === "class" ? "ຄລາດສ໌" : "ສ່ວນຕົວ"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            row.status === "absent" ? "destructive" : "outline"
                          }
                        >
                          {row.status === "absent" ? "ຂາດຮຽນ" : "ເຂົ້າຮຽນ"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="py-4 text-center text-muted-foreground text-xs">
          © {new Date().getFullYear()} {config.brandName}. ເບິ່ງໄດ້ຢ່າງດຽວ
          (ອ່ານຢ່າງດຽວ).
        </footer>
      </div>
    </div>
  );
}
